import React, { useCallback, useEffect, createContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMyProfile } from "../network/network";
import { handleApiError } from "../utils/errorHandler";
import {
  buildPortalAppConfig,
  readStoredPortalLanguage,
  storePortalLanguage,
} from "../utils/portalLanguage";

export const AppContext = createContext();

const AppContextProvider = ({ children }) => {
  const { t } = useTranslation();
  const [authUser, setAuthUser] = useState(false);
  const [displayWebView, setDisplayWebView] = useState("");
  const [fullPageLoading, setFullPageLoading] = useState(true);
  const [appConfig, setAppConfig] = useState(() =>
    buildPortalAppConfig({}, readStoredPortalLanguage()),
  );
  const [fullScreenPOS, setFullScreenPOS] = useState(false);
  const [isSiderCollapsed, setIsSiderCollapsed] = useState(false);

  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      setFullPageLoading(false);
    }, 1500);

    return () => {
      clearTimeout(loadingTimeout);
    };
  }, []);

  const refreshAuthUser = async () => {
    try {
      const response = await getMyProfile();

      if (response?.data?.success === false) {
        handleApiError(response, t);
        return null;
      }

      const user = response?.data?.data?.user || null;
      setAuthUser(user);
      return user;
    } catch (error) {
      handleApiError(error, t);
      return null;
    }
  };

  const setAppConfigWrapper = useCallback((obj = {}) => {
    const nextConfig = { ...obj };
    if (nextConfig.portal_language) {
      Object.assign(
        nextConfig,
        buildPortalAppConfig(nextConfig, nextConfig.portal_language),
      );
      storePortalLanguage(nextConfig.portal_language);
    }

    setAppConfig((currentConfig) => {
      const mergedConfig = { ...currentConfig, ...nextConfig };

      return JSON.stringify(currentConfig) === JSON.stringify(mergedConfig)
        ? currentConfig
        : mergedConfig;
    });
  }, []);

  useEffect(() => {
    document.body.classList.toggle("app_rtl", appConfig.direction === "rtl");
    document.body.classList.toggle("app_ltr", appConfig.direction !== "rtl");
  }, [appConfig.direction]);

  const roundOff = useCallback(
    (number) => {
      let places = 8;
      if (authUser.round_off === true) {
        places = 2;
      }

      return +(Math.round(number + "e+" + places) + "e-" + places);
    },
    [authUser],
  );

  return (
    <AppContext.Provider
      value={{
        authUser,
        setAuthUser,
        fullPageLoading,
        setFullPageLoading,
        appConfig,
        setAppConfigWrapper,
        roundOff,
        fullScreenPOS,
        setFullScreenPOS,
        displayWebView,
        setDisplayWebView,
        isSiderCollapsed,
        setIsSiderCollapsed,
        refreshAuthUser,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;
